// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters and/or current-time reads) that the useFluxStore bridge
// doesn't cover. Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import React, {useMemo} from 'react';
import cx from 'classnames';
import {Text} from '@albert-io/atomic';
import type {QuestionModelV3} from '@albert-io/models';
import {List} from 'immutable';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import {
  getSmallSupplement,
  SMALL_SUPPLEMENT_WRAPPER_CLASS,
  SupplementChip
} from 'client/components/QuestionTypes/shared/DraggableChip';

interface Props {
  question: QuestionModelV3;
}

export const CorrectAnswerSection = ({question}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);

  // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  const storeQuestion = useStoreHook((state) => state.question);

  const correctAnswerList = useMemo(() => {
    const items =
      storeQuestion?.getQuestionTypeAttributes()?.getIn(['ordering', 'items'], List()).toJS() || [];
    const slots =
      storeQuestion?.getQuestionTypeAttributes()?.getIn(['ordering', 'slots'], List()).toJS() || [];

    let validResponse: Record<string, string> = {};
    try {
      validResponse = storeQuestion?.getValidResponse()?.toJS() || {};
    } catch (_e) {
      // no valid response
    }

    const itemById: Record<string, any> = {};
    items.forEach((item: {id: string; value?: string | string[]}) => {
      itemById[item.id] = item;
    });

    // Correct order is defined by slots: for each slot position, find the mapped item
    return slots.map((slot: {id: string}, index: number) => {
      const itemId = validResponse[slot.id];
      const item = itemId ? itemById[itemId] : null;
      const id = item?.id || `word-${index}`;
      const value = item ? (Array.isArray(item.value) ? `[${item.value}]` : item.value || '') : '';
      return {id, value};
    });
  }, [storeQuestion]);

  return (
    <>
      <Text className='mt-x4 mb-x2'>Correct Order:</Text>
      <div className='mb-x2 gap-x3 rounded-x1 border-border-primary background-primary p-x2 pl-x3 flex flex-wrap items-center border border-solid'>
        {correctAnswerList.map((item) => {
          // Match the student answer's Item layout: constrain supplement (number-line/image) chips so
          // they don't expand to full width and squeeze the adjacent text chips into a clipped glyph.
          const isSupplement = !!getSmallSupplement(item.value);
          return (
            <div
              key={item.id}
              className={cx('flex items-stretch', isSupplement && SMALL_SUPPLEMENT_WRAPPER_CLASS)}
            >
              <div className={cx({'min-w-0 flex-1': isSupplement})}>
                <SupplementChip value={item.value} size='small' className='cursor-default!' />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CorrectAnswerSection;
